import { ChangeEvent, useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Accordion, AccordionDetails, AccordionSummary, FormControlLabel, Switch, Typography } from '@mui/material';
import { KeyboardArrowUp as KeyboardArrowUpIcon, Settings as SettingsIcon } from '@mui/icons-material';
import { endOfDay, startOfDay } from 'date-fns';

import { DateRangeSelect } from '@/components/common/date/DateRangeSelect';
import { TargetNameAndDescription, targetNameAndDescriptionStyle } from '@/components/common/TargetNameAndDescription';
import { HeaderNameAndIcons } from '@/components/common/HeaderNameAndIcons';
import { RoutesSummaryHeader } from './RoutesSummaryHeader';
import { ctxRoutes } from '@/contexts/RoutesCtx';
import { isPhoneOrHighScreen } from '@/utilities/sizeUtils';

function RoutesResultHeaderDateSelect() {
  const routeCtx = useContext(ctxRoutes);

  function onAccept(newFrom: Date, newTo: Date) {
    if (!newFrom || !newTo) return;
    const from = startOfDay(newFrom);
    const to = endOfDay(newTo);
    routeCtx?.setSelectedDates({ from, to });
  }

  if (!routeCtx) return;
  return (
    <DateRangeSelect setSelectedDates={onAccept} currentValue={routeCtx.selectedDates || { from: null, to: null }} />
  );
}

export function RoutesResultHeader() {
  const routeCtx = useContext(ctxRoutes);
  const { t } = useTranslation();
  const [isRotated, setIsRotated] = useState(false);
  const [show, setShow] = useState(false);
  const showRef = useRef(false);

  useEffect(() => {
    showRef.current =
      routeCtx?.showSummaryTooltip &&
      routeCtx?.highlightedRoutes?.length !== 0 &&
      JSON.parse(sessionStorage.getItem('showRoutesSummaryTooltip') ?? 'true');
  }, [routeCtx?.showSummaryTooltip, routeCtx?.highlightedRoutes?.length]);

  useEffect(() => {
    if (showRef.current) {
      if (!show) {
        if (showRef.current) {
          setShow(showRef.current);
        }
      }
    } else setShow(false);
  }, [routeCtx?.showSummaryTooltip, routeCtx?.highlightedRoutes?.length]);

  function handlePoiNamesSelect(event: ChangeEvent<HTMLInputElement>) {
    const checked = event.target.checked;
    routeCtx?.setPoiNamesEnabled(checked);
  }

  function handleChange(_event: unknown, expanded: boolean) {
    setIsRotated(expanded);
  }

  if (!routeCtx || !routeCtx.selectedTarget) return;

  return (
    <>
      <div
        className={`route-result-header ${routeCtx.routesLoading ? 'disabled' : ''}`}
        data-qa-element-id="route_result_header"
      >
        {isPhoneOrHighScreen() ? (
          <HeaderNameAndIcons target={routeCtx.selectedTarget} onClose={() => routeCtx.setIsBottomSheetOpen(false)} />
        ) : (
          <TargetNameAndDescription
            targetName={routeCtx.selectedTarget?.name}
            targetDescription={routeCtx.selectedTarget?.description}
            styleClassName={targetNameAndDescriptionStyle(undefined, undefined)}
          />
        )}
        {!isPhoneOrHighScreen() && <RoutesResultHeaderDateSelect />}
        <Accordion
          onChange={handleChange}
          className="route-settings-accordion"
          data-qa-element-id="route_settings_dropdown"
          disableGutters
        >
          <AccordionSummary
            id="route-settings"
            expandIcon={
              <KeyboardArrowUpIcon
                sx={{
                  transform: 'rotate(180deg)',
                  transition: 'transform 0.3s ease',
                }}
                data-qa-element-id="open_accordion_icon"
              />
            }
          >
            <SettingsIcon
              id="route-settings-icon"
              data-qa-element-id="route_settings_icon"
              sx={{
                transform: isRotated ? 'rotate(120deg)' : 'rotate(0deg)',
                transition: 'transform 0.8s ease',
              }}
            />
            <Typography variant="subtitle2" sx={{ py: 1 }}>
              {t('general.settings')}
            </Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ display: 'flex', flexDirection: 'column' }} data-qa-element-id="route_settings_panel">
            <FormControlLabel
              control={
                <Switch
                  onChange={handlePoiNamesSelect}
                  checked={routeCtx.poiNamesEnabled}
                  data-qa-element-id="poi_label_switch"
                />
              }
              label={t('routes.onlyPoiAddresses')}
            />
          </AccordionDetails>
        </Accordion>
        {show && (
          <div className="route-summary-highlight-tooltip" data-qa-element-id="deselect_all_routes_tooltip">
            {t('routes.routeSummaryTooltip')}
          </div>
        )}
      </div>
      {isPhoneOrHighScreen() && <RoutesSummaryHeader />}
    </>
  );
}
